import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTasksLightweight, getTask, getTaskCounts } from '@/utils/api';
import { toast } from 'sonner';
import { Search, Plus, ChevronLeft, ChevronRight, ChevronDown, Filter, Clock, AlertTriangle, Loader2, GripVertical, X, MessageSquare, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TaskListItem from '@/components/TaskListItem';
import TaskSummaryView from '@/components/TaskSummaryView';
import ChatPanel from '@/components/ChatPanel';
import TaskDetailForm from '@/components/TaskDetailForm';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { STATUS_LABELS } from '@/utils/constants';

const ThreePanelTaskPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDeadline, setFilterDeadline] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [taskListCollapsed, setTaskListCollapsed] = useState(false);
  const [counts, setCounts] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedRevisions, setExpandedRevisions] = useState(new Set());

  const toggleRevisionExpand = (taskId, e) => {
    e.stopPropagation();
    setExpandedRevisions(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const { sidebarRoots, sidebarChildrenMap } = useMemo(() => {
    const taskMap = new Map();
    filteredTasks.forEach(t => taskMap.set(t.id, t));
    
    // Group all tasks in filteredTasks by their root lineage ID
    const lineageGroups = new Map();
    filteredTasks.forEach(t => {
      const rootId = t.parent_task_id || t.id;
      if (!lineageGroups.has(rootId)) {
        lineageGroups.set(rootId, []);
      }
      lineageGroups.get(rootId).push(t);
    });
    
    const roots = [];
    const childrenMap = new Map();
    const processedRoots = new Set();
    
    filteredTasks.forEach(t => {
      const rootId = t.parent_task_id || t.id;
      if (processedRoots.has(rootId)) return;
      
      const lineage = lineageGroups.get(rootId) || [];
      // Sort lineage by revision_suffix ascending so the original (0) is first and latest is last
      lineage.sort((a, b) => (a.revision_suffix || 0) - (b.revision_suffix || 0));
      
      if (lineage.length > 0) {
        const latest = lineage[lineage.length - 1];
        roots.push(latest);
        
        const previousRevisions = lineage.slice(0, lineage.length - 1);
        if (previousRevisions.length > 0) {
          childrenMap.set(latest.id, previousRevisions);
        }
      }
      
      processedRoots.add(rootId);
    });
    
    return { sidebarRoots: roots, sidebarChildrenMap: childrenMap };
  }, [filteredTasks]);

  
  // Resizable chat panel state
  const [chatWidth, setChatWidth] = useState(380);
  const [isResizing, setIsResizing] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showMobileTaskList, setShowMobileTaskList] = useState(true);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle chat panel resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    const containerWidth = window.innerWidth;
    const newWidth = containerWidth - e.clientX;
    // Min 280px, max 600px
    setChatWidth(Math.min(Math.max(newWidth, 280), 600));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterDeadline, filterUnread]);

  // Handle selected task from URL param (e.g., after creating a new task)
  useEffect(() => {
    const selectedId = searchParams.get('selected');
    if (selectedId && tasks.length > 0) {
      const taskToSelect = tasks.find(t => t.id === selectedId);
      if (taskToSelect) {
        handleTaskSelect(taskToSelect);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('selected');
        setSearchParams(newParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchParams]);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchQuery]);

  const fetchCounts = async () => {
    try {
      const response = await getTaskCounts();
      setCounts(response.data);
    } catch (error) {
      console.error('Failed to load counts');
    }
  };

  const fetchTasks = async () => {
    try {
      const params = {};
      if (filterStatus) params.task_status = filterStatus;
      if (filterDeadline) params.deadline = filterDeadline;
      if (filterUnread) params.has_unread = true;
      
      // Batch: fetch tasks and counts in parallel
      const [tasksRes, countsRes] = await Promise.all([
        getTasksLightweight(params),
        getTaskCounts()
      ]);
      
      const newTasks = tasksRes.data;
      setTasks(newTasks);
      setCounts(countsRes.data);
      
      // Auto-select first task only if nothing is currently selected
      if (newTasks.length > 0 && !selectedTask) {
        try {
          const detailRes = await getTask(newTasks[0].id);
          setSelectedTask(detailRes.data);
        } catch (err) {
          console.error('Failed to load task detail:', err);
        }
      }
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = () => {
    let filtered = [...tasks];

    if (searchQuery) {
      filtered = filtered.filter(
        (task) =>
          task.ks_design_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.internal_cad_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.party_cad_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredTasks(filtered);
  };

  const handleTaskSelect = async (task) => {
    try {
      const response = await getTask(task.id);
      setSelectedTask(response.data);
      setShowCreateForm(false);
    } catch (error) {
      if (error.response?.status !== 401) toast.error('Failed to load task details');
    }
  };

  const handleTaskUpdate = () => {
    // Refresh the task list
    fetchTasks();
    setShowCreateForm(false);
    // Re-fetch the currently selected task to get latest data
    if (selectedTask?.id) {
      getTask(selectedTask.id)
        .then((response) => setSelectedTask(response.data))
        .catch(() => {});
    }
  };

  const handleCreateNew = () => {
    // Navigate to the full-page create form
    navigate('/tasks/new');
  };

  const canCreateTask = true; // All roles can create tasks

  const clearFilters = () => {
    setFilterStatus('');
    setFilterDeadline('');
    setFilterUnread(false);
    setSearchQuery('');
  };

  return (
    <CollapsibleLayout>
      <div className="h-[calc(100vh-48px)] flex bg-background relative">
        {/* Mobile Header Bar */}
        {isMobile && selectedTask && !showCreateForm && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border p-2 flex items-center justify-between md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedTask(null); setShowMobileTaskList(true); }}
            >
              <ChevronLeft size={18} />
              Back
            </Button>
            <span className="text-sm font-medium truncate flex-1 text-center">{selectedTask.internal_cad_no}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMobileChat(!showMobileChat)}
              className="relative"
            >
              <MessageSquare size={18} />
            </Button>
          </div>
        )}

        {/* Filter Sidebar - Hidden on mobile */}
        {showFilters && !taskListCollapsed && !isMobile && (
          <div className="w-44 bg-white border-r border-border flex flex-col">
            <div className="p-3 border-b border-border">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filters</h3>
            </div>
            
            {/* Deadline Filters */}
            <div className="p-3 border-b border-border/50">
              <h4 className="text-xs font-medium text-foreground mb-2">By Deadline</h4>
              <div className="space-y-1">
                {counts?.overdue > 0 && (
                  <button
                    onClick={() => setFilterDeadline(filterDeadline === 'overdue' ? '' : 'overdue')}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                      filterDeadline === 'overdue' ? 'bg-red-50 text-red-700' : 'hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle size={12} className="text-red-500" />
                      Overdue
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'overdue' ? 'bg-red-100' : 'bg-muted'}`}>
                      {counts?.overdue || 0}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setFilterDeadline(filterDeadline === 'today' ? '' : 'today')}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                    filterDeadline === 'today' ? 'bg-orange-50 text-orange-700' : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Clock size={12} className="text-orange-500" />
                    Due Today
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'today' ? 'bg-orange-100' : 'bg-muted'}`}>
                    {counts?.due_today || 0}
                  </span>
                </button>
                <button
                  onClick={() => setFilterDeadline(filterDeadline === 'this_week' ? '' : 'this_week')}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                    filterDeadline === 'this_week' ? 'bg-gold text-navy font-medium' : 'hover:bg-muted text-muted-foreground'
                  }`}
                >
                  <span>This Week</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterDeadline === 'this_week' ? 'bg-white/30' : 'bg-muted'}`}>
                    {counts?.due_this_week || 0}
                  </span>
                </button>
              </div>
            </div>

            {/* Status Filters */}
            <div className="p-3 flex-1 overflow-y-auto">
              <h4 className="text-xs font-medium text-foreground mb-2">By Status</h4>
              <div className="space-y-1">
                {counts?.by_status && (() => {
                  // Canonical priority order: when several legacy statuses
                  // share a label (e.g. "CAD in Progress" = cad_in_progress
                  // OR in_progress), send the canonical key to the backend so
                  // it expands via _smap to the full legacy alias group. Else
                  // statuses[0] could be a legacy key that bypasses the group
                  // expansion and silently filters to one row.
                  const CANONICAL = ['pre_cad', 'cad_in_progress', 'cad_completed', 'accepted', 'rejected', 'revision_requested'];
                  const merged = {};
                  Object.entries(counts.by_status).forEach(([status, count]) => {
                    const label = STATUS_LABELS[status] || status;
                    if (!merged[label]) merged[label] = { statuses: [], count: 0 };
                    merged[label].statuses.push(status);
                    merged[label].count += count;
                  });
                  return Object.entries(merged).map(([label, { statuses, count }]) => {
                    const canonical = CANONICAL.find(c => statuses.includes(c)) || statuses[0];
                    return (
                      <button
                        key={label}
                        onClick={() => setFilterStatus(statuses.includes(filterStatus) ? '' : canonical)}
                        className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                          statuses.includes(filterStatus) ? 'bg-navy text-white' : 'hover:bg-muted text-muted-foreground'
                        }`}
                      >
                        <span className="truncate">{label}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${statuses.includes(filterStatus) ? 'bg-white/20' : 'bg-muted'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Unread */}
            {counts?.unread > 0 && (
              <div className="p-3 border-t border-border/50">
                <button
                  onClick={() => setFilterUnread(!filterUnread)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${filterUnread ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                  data-testid="unread-filter-btn"
                >
                  <span>{filterUnread ? 'Showing Unread Only' : 'Unread Messages'}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${filterUnread ? 'bg-blue-500' : 'bg-blue-100'}`}>{counts?.unread}</span>
                </button>
              </div>
            )}

            {(filterStatus || filterDeadline || filterUnread) && (
              <div className="p-3 border-t border-border/50">
                <button
                  onClick={clearFilters}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Task List - Full width on mobile when no task selected */}
        <div
          className={`bg-white border-r border-border flex flex-col transition-all duration-300 ${
            isMobile 
              ? (selectedTask && !showMobileTaskList ? 'hidden' : 'w-full') 
              : (taskListCollapsed ? 'w-12' : 'w-64')
          }`}
        >
          {taskListCollapsed ? (
            <div className="flex items-center justify-center p-3 border-b border-border">
              <button
                onClick={() => setTaskListCollapsed(false)}
                className="p-2 hover:bg-muted rounded-md"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border bg-navy">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-white">CAD Files</h2>
                  <div className="flex items-center gap-1">
                    <span
                      className="px-1.5 py-0.5 rounded text-xs font-medium bg-gold text-navy"
                    >
                      {filteredTasks.length}
                    </span>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`p-1 rounded-md ${showFilters ? 'bg-white/20' : 'hover:bg-white/10'} text-white`}
                      title="Toggle filters"
                    >
                      <Filter size={14} />
                    </button>
                    <button
                      onClick={() => setTaskListCollapsed(true)}
                      className="p-1 hover:bg-white/10 rounded-md text-white"
                    >
                      <ChevronLeft size={14} />
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>

                {canCreateTask && (
                  <Button
                    onClick={handleCreateNew}
                    className="w-full mt-2 h-8 text-xs font-medium bg-gold text-navy"
                    data-testid="new-cad-btn"
                  >
                    <Plus size={14} className="mr-1" />
                    New CAD
                  </Button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground text-xs flex flex-col items-center">
                    <Loader2 size={18} className="animate-spin mb-2" />
                    Loading...
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-xs">
                    {searchQuery || filterStatus || filterDeadline ? 'No tasks match filters' : 'No tasks yet'}
                  </div>
                ) : (
                  <div>
                    {sidebarRoots.map((task) => {
                      const children = sidebarChildrenMap.get(task.id) || [];
                      const hasChildren = children.length > 0;
                      const isExpanded = expandedRevisions.has(task.id);
                      
                      return (
                        <div key={task.id} className="border-b border-border/30 last:border-b-0">
                          <div className="relative group">
                            <TaskListItem
                              task={task}
                              isSelected={selectedTask?.id === task.id}
                              onClick={() => {
                                handleTaskSelect(task);
                                if (isMobile) setShowMobileTaskList(false);
                              }}
                              currentUserId={user?.id}
                            />
                            {hasChildren && (
                              <button
                                onClick={(e) => toggleRevisionExpand(task.id, e)}
                                className="absolute right-2 top-2 p-1 rounded hover:bg-muted border border-transparent hover:border-border text-muted-foreground z-10"
                                title={isExpanded ? "Hide revisions" : "Show revisions"}
                              >
                                <ChevronDown size={14} className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>
                          {hasChildren && isExpanded && (
                            <div className="bg-slate-50/50 pl-4 border-l border-dashed border-border py-1 space-y-1">
                              {children.map((child) => (
                                <div key={child.id} className="flex items-center gap-1">
                                  <span className="text-muted-foreground text-[10px] select-none font-bold pl-1">↳</span>
                                  <div className="flex-1 min-w-0">
                                    <TaskListItem
                                      task={child}
                                      isSelected={selectedTask?.id === child.id}
                                      onClick={() => {
                                        handleTaskSelect(child);
                                        if (isMobile) setShowMobileTaskList(false);
                                      }}
                                      currentUserId={user?.id}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content - Hidden on mobile when task list is showing */}
        <div className={`flex-1 flex flex-col min-w-0 ${isMobile && showMobileTaskList ? 'hidden' : ''} ${isMobile && selectedTask ? 'pt-12' : ''}`}>
          {showCreateForm ? (
            <div className="h-full bg-white overflow-hidden">
              <TaskDetailForm
                task={null}
                onUpdate={handleTaskUpdate}
                onCancel={() => {
                  setShowCreateForm(false);
                  if (tasks.length > 0) {
                    setSelectedTask(tasks[0]);
                  }
                  if (isMobile) setShowMobileTaskList(true);
                }}
              />
            </div>
          ) : selectedTask ? (
            <TaskSummaryView task={selectedTask} onUpdate={handleTaskUpdate} />
          ) : (
            <div className="flex items-center justify-center h-full bg-white">
              <div className="text-center text-muted-foreground">
                <p className="text-base">No task selected</p>
                <p className="text-xs mt-1">Select a CAD file from the list</p>
              </div>
            </div>
          )}
        </div>

        {/* Resizable Chat Panel - Desktop only */}
        {selectedTask && !showCreateForm && !isMobile && (
          <>
            {chatCollapsed ? (
              <div className="w-12 bg-white border-l border-border flex flex-col items-center py-4 gap-4 flex-shrink-0">
                <button
                  onClick={() => setChatCollapsed(false)}
                  className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                  title="Expand Discussion"
                  data-testid="expand-chat-btn"
                >
                  <ChevronLeft size={18} />
                </button>
                <div 
                  className="flex flex-col items-center gap-2 select-none cursor-pointer" 
                  onClick={() => setChatCollapsed(false)}
                >
                  <MessageSquare size={16} className="text-muted-foreground" />
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider [writing-mode:vertical-lr] rotate-180">
                    Discussion
                  </span>
                </div>
              </div>
            ) : (
              <>
                {/* Resize Handle */}
                <div
                  onMouseDown={handleMouseDown}
                  className={`w-1 cursor-ew-resize flex items-center justify-center hover:bg-amber-400 transition-colors ${isResizing ? 'bg-gold' : 'bg-border'}`}
                  title="Drag to resize"
                >
                  <GripVertical size={12} className="text-muted-foreground" />
                </div>
                <div style={{ width: chatWidth }} className="transition-none flex flex-col h-full">
                  <ChatPanel 
                    taskId={selectedTask.id} 
                    taskName={selectedTask.product_name || selectedTask.client_name} 
                    onCollapse={() => setChatCollapsed(true)}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* Mobile Chat Drawer */}
        {isMobile && selectedTask && showMobileChat && (
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowMobileChat(false)}>
            <div 
              className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-3 border-b bg-navy">
                <h3 className="text-white font-medium text-sm">Chat</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowMobileChat(false)} className="text-white hover:bg-white/10">
                  <X size={18} />
                </Button>
              </div>
              <div className="h-[calc(100vh-52px)]">
                <ChatPanel taskId={selectedTask.id} taskName={selectedTask.product_name || selectedTask.client_name} />
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleLayout>
  );
};

export default ThreePanelTaskPage;
